// piano-frontend/src/components/SchedulePlanner.js
// 스케줄 플래너 (관리자 페이지 '플래너' 탭의 전체 페이지 인라인 뷰) —
// 고정제 회원들의 주간 고정 스케줄을 한 화면에서 이동/맞교환(스왑)/체인으로 재배치하고,
// 최종 상태를 서버 시뮬레이션(batch)으로 검증한 뒤 bulk-update 한 번(1 트랜잭션)으로 적용한다.
// 기존 3단계(한 명 비우기 → 다른 회원 이동 → 원래 회원 재배치)를 1단계로 줄이는 도구.
import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  Box, Typography, DialogTitle, DialogContent,
  Button, CircularProgress, IconButton, Stack, Alert, Divider,
  Dialog as InnerDialog, DialogActions, useMediaQuery
} from "@mui/material";
import { SwapHoriz, ArrowForward, DeleteOutline, TouchApp } from "@mui/icons-material";
import api from "../api/axios";
import { useDarkMode } from "../App";
import MakeupDecisionDialog from "./MakeupDecisionDialog";
import { computeTransitionWeek, fmtDateLocal } from "../utils/transitionWeek";

const DAYS = ["MON", "TUE", "WED", "THU", "FRI", "SAT"];
const DAY_LABEL = { MON: "월", TUE: "화", WED: "수", THU: "목", FRI: "금", SAT: "토" };

const toMin = (t) => { const [h, m] = String(t).split(":").map(Number); return h * 60 + m; };
const toTime = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;

const WD_LABEL = ["일", "월", "화", "수", "목", "금", "토"];
// "YYYY-MM-DD" → "M/D(요일)"
const fmtDate = (iso) => {
  const [y, m, d] = String(iso).split("-").map(Number);
  if (!y || !m || !d) return String(iso);
  return `${m}/${d}(${WD_LABEL[new Date(y, m - 1, d).getDay()]})`;
};
// 시뮬레이션 경고의 점유 유형 표기: 레슨실 점유는 방 이름, 관리자/선생님 예약은 블록, 그 외 회원 레슨
const occupantType = (o) =>
  o.room ? o.room : (o.role === "ADMIN" || o.role === "TEACHER") ? "블록" : "레슨";

function SchedulePlanner({ onApplied }) {
  const { darkMode } = useDarkMode();

  const [loading, setLoading] = useState(false);
  const [membersMap, setMembersMap] = useState(new Map()); // memberId -> { name, isActive, schedules:[{day,start,end,teacher_id,teacherName}] }
  const [teachers, setTeachers] = useState([]);
  const [moves, setMoves] = useState([]); // [{ memberId, memberName, from:{day,start,end,teacher_id,teacherName}, to:{...} }]
  const [moving, setMoving] = useState(null); // { memberId, memberName, slot:{day,start,end,teacher_id,teacherName} }
  const [conflictPrompt, setConflictPrompt] = useState(null); // { target:{day,time}, to, mover, occupant }
  const [teacherPick, setTeacherPick] = useState(null); // { day, time, mover, options:[{teacher, occupants}] }
  const [sim, setSim] = useState({ loading: false, result: null });
  const [applying, setApplying] = useState(false);
  const [notice, setNotice] = useState(null);
  const [makeupQueue, setMakeupQueue] = useState(null); // { queue:[decision...], collected:{memberId:choice} }
  const [transitionPrompt, setTransitionPrompt] = useState(null); // 전환 주 초과 회원 목록 [{ memberName, normal, ...tw }]

  // PC(md 이상) 전용 드래그 앤 드롭 — 기존 클릭 흐름(setMoving → stageMove) 위에 입력 방식만 추가 (로직 공유)
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up("md"));
  const [dragging, setDragging] = useState(false); // 드래그 중 '이동 중' 배너 억제 (배너 삽입으로 그리드가 밀려 드롭 좌표가 어긋나는 것 방지)
  const [dragOverCell, setDragOverCell] = useState(null); // 드래그 호버 중인 칸 "DAY|HH:MM"
  const dropHandledRef = useRef(false); // 칸 위 드롭 성사 여부 — dragend에서 이동 모드 해제 판단

  const fetchBoard = useCallback(async () => {
    setLoading(true);
    try {
      const [slotsRes, teachersRes] = await Promise.all([
        api.get("/members/schedules/slots"),
        api.get("/teachers"),
      ]);
      const map = new Map();
      const slots = slotsRes.data.slots || {};
      for (const day of Object.keys(slots)) {
        for (const time of Object.keys(slots[day])) {
          for (const e of slots[day][time]) {
            // 비활성 선생님 + 비활성 회원의 슬롯은 잔재 데이터 — 보드에서 숨김
            // (활성 회원이 비활성 선생님 슬롯에 남은 경우는 옮겨야 할 대상이므로 경고 표시로 유지)
            if (e.teacherActive === false && !e.isActive) continue;
            if (!map.has(e.memberId)) {
              map.set(e.memberId, { name: e.memberName, isActive: e.isActive, schedules: [] });
            }
            map.get(e.memberId).schedules.push({
              day,
              start: e.startTime,
              end: e.endTime,
              teacher_id: e.teacherId,
              teacherName: e.teacherName,
              teacherActive: e.teacherActive !== false,
            });
          }
        }
      }
      setMembersMap(map);
      setTeachers(teachersRes.data);
    } catch (err) {
      console.error("스케줄 보드 조회 실패:", err);
      setNotice({ severity: "error", message: "스케줄을 불러오지 못했습니다." });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setMoves([]);
    setMoving(null);
    setSim({ loading: false, result: null });
    setNotice(null);
    fetchBoard();
  }, [fetchBoard]);

  // 스테이징된 이동을 순서대로 적용한 회원의 '최종' 스케줄
  const effectiveOf = useCallback((memberId) => {
    const base = membersMap.get(memberId)?.schedules || [];
    let schedules = base.map((s) => ({ ...s }));
    for (const mv of moves) {
      if (mv.memberId !== memberId) continue;
      schedules = schedules.map((s) => {
        if (s.day === mv.from.day && String(s.start).slice(0, 5) === String(mv.from.start).slice(0, 5) && s.teacher_id === mv.from.teacher_id) {
          const dur = toMin(String(s.end).slice(0, 5)) - toMin(String(s.start).slice(0, 5));
          return {
            ...s,
            day: mv.to.day,
            start: mv.to.start,
            end: toTime(toMin(mv.to.start) + dur),
            teacher_id: mv.to.teacher_id,
            teacherName: mv.to.teacherName,
            teacherActive: true, // 이동 대상은 활성(근무 중) 선생님만 제안됨
          };
        }
        return s;
      });
    }
    return schedules;
  }, [membersMap, moves]);

  // 보드 셀 데이터: `${day}|${time}` -> 항목들 (최종 상태 기준)
  const board = useMemo(() => {
    const cells = new Map();
    const push = (day, time, entry) => {
      const key = `${day}|${time}`;
      if (!cells.has(key)) cells.set(key, []);
      cells.get(key).push(entry);
    };
    for (const [memberId, info] of membersMap.entries()) {
      const effective = effectiveOf(memberId);
      const movedSlots = moves.filter((m) => m.memberId === memberId);
      for (const s of effective) {
        const time = String(s.start).slice(0, 5);
        const staged = movedSlots.some((m) => m.to.day === s.day && m.to.start === time && m.to.teacher_id === s.teacher_id);
        push(s.day, time, { memberId, name: info.name, isActive: info.isActive, teacher_id: s.teacher_id, teacherName: s.teacherName, teacherActive: s.teacherActive !== false, slot: { ...s, start: time }, staged, ghost: false });
      }
      // 이동으로 비게 된 원래 자리는 잔상(ghost)으로 표시
      for (const mv of movedSlots) {
        push(mv.from.day, String(mv.from.start).slice(0, 5), { memberId, name: info.name, isActive: info.isActive, teacher_id: mv.from.teacher_id, teacherName: mv.from.teacherName, slot: mv.from, staged: false, ghost: true });
      }
    }
    return cells;
  }, [membersMap, moves, effectiveOf]);

  // 표시할 시간 행: 선생님 근무시간과 회원 슬롯을 모두 덮는 범위에서 30분 단위 생성 (데이터 없으면 10:00~22:00)
  const timeSlots = useMemo(() => {
    let min = Infinity, max = -Infinity;
    teachers.forEach((t) => (t.schedules || []).forEach((s) => {
      min = Math.min(min, toMin(String(s.start).slice(0, 5)));
      max = Math.max(max, toMin(String(s.end).slice(0, 5)));
    }));
    for (const info of membersMap.values()) {
      for (const s of info.schedules) {
        min = Math.min(min, toMin(String(s.start).slice(0, 5)));
        max = Math.max(max, toMin(String(s.end).slice(0, 5)));
      }
    }
    if (!isFinite(min) || !isFinite(max)) { min = 10 * 60; max = 22 * 60; }
    const out = [];
    for (let m = min; m < max; m += 30) out.push(toTime(m));
    return out;
  }, [teachers, membersMap]);

  // 해당 요일·시간에 근무하는 선생님 목록 (기본 주간 스케줄 기준 — 최종 판정은 서버 시뮬레이션)
  const teachersWorkingAt = useCallback((day, time) => {
    return teachers.filter((t) =>
      (t.schedules || []).some((s) => s.day === day && time >= String(s.start).slice(0, 5) && time < String(s.end).slice(0, 5))
    );
  }, [teachers]);

  // 특정 선생님 칸의 점유 회원 (최종 상태 기준, 잔상·이동 회원 본인 제외)
  const occupantsOf = (day, time, teacherId, excludeMemberId) =>
    (board.get(`${day}|${time}`) || []).filter(
      (e) => !e.ghost && e.teacher_id === teacherId && e.memberId !== excludeMemberId
    );

  const buildTo = (mover, day, time, teacher) => ({
    day,
    start: time,
    end: toTime(toMin(time) + (toMin(String(mover.slot.end).slice(0, 5)) - toMin(String(mover.slot.start).slice(0, 5)))),
    teacher_id: teacher.id,
    teacherName: teacher.name,
  });

  // 선생님이 정해진 뒤: 빈자리면 바로 확정, 점유돼 있으면 맞바꾸기/체인 프롬프트
  const proceedWithTeacher = (mover, day, time, teacher) => {
    const to = buildTo(mover, day, time, teacher);
    const occupants = occupantsOf(day, time, teacher.id, mover.memberId);
    if (occupants.length > 0) {
      setConflictPrompt({ target: { day, time }, to, mover, occupant: occupants[0] });
      return;
    }
    // 빈자리 이동이 이 칩의 '원래 자리'(스테이징 전 위치) 복귀라면 이동을 쌓지 않고 스테이징 취소로 처리.
    // 제자리 드롭/클릭 포함 — 무의미한 net-zero 계획이 적용(예약 재생성)으로 이어지는 것을 막는다.
    const stagedIdx = moves.findIndex((m) => m.memberId === mover.memberId
      && m.to.day === mover.slot.day
      && String(m.to.start).slice(0, 5) === String(mover.slot.start).slice(0, 5)
      && m.to.teacher_id === mover.slot.teacher_id);
    const origin = stagedIdx >= 0 ? moves[stagedIdx].from : mover.slot;
    if (to.day === origin.day && to.start === String(origin.start).slice(0, 5) && to.teacher_id === origin.teacher_id) {
      if (stagedIdx >= 0) setMoves((prev) => prev.filter((_, i) => i !== stagedIdx));
      setMoving(null);
      return;
    }
    commitMove(mover, to);
  };

  // 칸 클릭: 근무 선생님이 1명이면 바로 진행, 여러 명이면 선생님 선택 (다른 선생님 자리로도 이동 가능)
  const stageMove = (mover, day, time) => {
    const working = teachersWorkingAt(day, time);
    if (working.length === 0) return;
    if (working.length === 1) {
      proceedWithTeacher(mover, day, time, working[0]);
      return;
    }
    setTeacherPick({
      day,
      time,
      mover,
      options: working.map((t) => ({ teacher: t, occupants: occupantsOf(day, time, t.id, mover.memberId) })),
    });
  };

  const commitMove = (mover, to, extraMove = null) => {
    setMoves((prev) => {
      // 같은 회원의 같은 출발 슬롯 이동은 교체
      const next = prev.filter(
        (m) => !(m.memberId === mover.memberId && m.from.day === mover.slot.day && String(m.from.start).slice(0, 5) === String(mover.slot.start).slice(0, 5) && m.from.teacher_id === mover.slot.teacher_id)
      );
      next.push({ memberId: mover.memberId, memberName: mover.memberName, from: { ...mover.slot, start: String(mover.slot.start).slice(0, 5) }, to });
      if (extraMove) next.push(extraMove);
      return next;
    });
    setMoving(null);
  };

  // 서로 맞바꾸기: 상대를 이동 회원의 원래 자리로
  const handleSwap = () => {
    const { mover, to, occupant } = conflictPrompt;
    const occupantMove = {
      memberId: occupant.memberId,
      memberName: occupant.name,
      from: { ...occupant.slot, start: String(occupant.slot.start).slice(0, 5) },
      to: {
        day: mover.slot.day,
        start: String(mover.slot.start).slice(0, 5),
        end: String(mover.slot.end).slice(0, 5),
        teacher_id: occupant.teacher_id,
        teacherName: occupant.teacherName,
      },
    };
    commitMove(mover, to, occupantMove);
    setConflictPrompt(null);
  };

  // 체인: 이동을 확정하고 상대를 다음 이동 대상으로
  const handleChain = () => {
    const { mover, to, occupant } = conflictPrompt;
    commitMove(mover, to);
    setMoving({ memberId: occupant.memberId, memberName: occupant.name, slot: { ...occupant.slot, start: String(occupant.slot.start).slice(0, 5) } });
    setConflictPrompt(null);
  };

  const removeMove = (idx) => setMoves((prev) => prev.filter((_, i) => i !== idx));

  // 변경 계획 → bulk changes (회원별 최종 스케줄 전체)
  const buildChanges = useCallback(() => {
    const affected = [...new Set(moves.map((m) => m.memberId))];
    return affected.map((id) => ({
      member_id: id,
      new_schedules: effectiveOf(id).map((s) => ({
        day: s.day,
        start: String(s.start).slice(0, 5),
        end: String(s.end).slice(0, 5),
        teacher_id: s.teacher_id,
      })),
    }));
  }, [moves, effectiveOf]);

  // 이동 변경 시 서버 배치 시뮬레이션 (디바운스)
  useEffect(() => {
    if (moves.length === 0) {
      setSim({ loading: false, result: null });
      return;
    }
    setSim((s) => ({ ...s, loading: true }));
    const timer = setTimeout(async () => {
      try {
        const res = await api.post("/members/schedules/simulate", { changes: buildChanges() });
        setSim({ loading: false, result: res.data });
      } catch (err) {
        console.error("시뮬레이션 실패:", err);
        setSim({ loading: false, result: null });
      }
    }, 450);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(moves)]);

  // 일괄 적용은 오늘부터 반영되므로, 요일이 이동하는 회원은 전환 주(이번 주)가 평소
  // 주당 횟수와 달라질 수 있다(초과: 지난 기존 요일 수업 + 새 요일 수업 공존 / 부족:
  // 기존 요일은 삭제되고 새 요일은 이미 지남) → 적용 전 안내.
  // 보강 결정 수집 후 재적용 경로(handleApply 직접 호출)에서는 다시 묻지 않는다.
  const handleApplyClick = () => {
    const todayStr = fmtDateLocal(new Date());
    const affected = [...new Set(moves.map((m) => m.memberId))];
    const diffs = [];
    for (const id of affected) {
      const oldDays = (membersMap.get(id)?.schedules || []).map((s) => s.day);
      const newDays = effectiveOf(id).map((s) => s.day);
      if (oldDays.length === 0 || newDays.length === 0) continue;
      const tw = computeTransitionWeek(todayStr, oldDays, newDays);
      if (tw && tw.total !== newDays.length) {
        diffs.push({ memberName: membersMap.get(id)?.name || `회원#${id}`, normal: newDays.length, ...tw });
      }
    }
    if (diffs.length > 0) setTransitionPrompt(diffs);
    else handleApply();
  };

  const handleApply = async (makeupDecisions = {}) => {
    setApplying(true);
    try {
      const res = await api.post("/members/schedules/bulk-update", {
        changes: buildChanges(),
        makeup_decisions: makeupDecisions,
      });
      setMakeupQueue(null); // 결정 다이얼로그가 열려 있었다면 결과 표시와 동시에 닫기
      onApplied && onApplied(res.data);
      setMoves([]);
      setMoving(null);
      setSim({ loading: false, result: null });
      fetchBoard();
    } catch (err) {
      const data = err?.response?.data;
      // 이미 사용된 보강 티켓 → 회원별 관리자 결정 필요 (한 명씩 결정 다이얼로그)
      if (err?.response?.status === 409 && data?.requiresMakeupDecision && (data.decisions || []).length > 0) {
        setMakeupQueue({ queue: data.decisions, collected: makeupDecisions });
      } else {
        setMakeupQueue(null);
        setNotice({
          severity: "error",
          message: data?.message || "일괄 변경에 실패했습니다.",
        });
      }
    } finally {
      setApplying(false);
    }
  };

  // 결정 다이얼로그: 큐의 첫 회원부터 순서대로 결정을 수집한 뒤 재적용.
  // 마지막 결정은 다이얼로그를 '적용 중' 상태로 유지하다가 응답 도착과 동시에 결과로 전환한다.
  const handleMakeupDecided = (choice) => {
    if (!makeupQueue) return;
    const current = makeupQueue.queue[0];
    const collected = { ...makeupQueue.collected, [current.member_id]: choice };
    const rest = makeupQueue.queue.slice(1);
    if (rest.length > 0) {
      setMakeupQueue({ queue: rest, collected });
    } else {
      handleApply(collected);
    }
  };

  const canApply = moves.length > 0 && sim.result?.canProceed && !sim.loading && !applying;
  const allConflicts = (sim.result?.results || []).flatMap((r) => (r.conflicts || []).map((c) => ({ ...c, memberName: r.member?.name })));
  const allWarnings = (sim.result?.results || []).flatMap((r) => (r.warnings || []).map((w) => ({ ...w, memberName: r.member?.name })));

  // 서버 시뮬레이션이 충돌로 지목한 칸 (빨간 테두리 표시)
  const simConflictCells = useMemo(() => {
    const set = new Set();
    for (const r of sim.result?.results || []) {
      for (const c of r.conflicts || []) {
        if (c.day && c.time) set.add(`${c.day}|${c.time}`);
      }
    }
    return set;
  }, [sim.result]);

  const chipColor = {
    base: { bgcolor: darkMode ? "rgba(144,202,249,0.16)" : "#e3f2fd", color: darkMode ? "#90caf9" : "#1565c0" },
    staged: { bgcolor: darkMode ? "rgba(76,175,80,0.2)" : "#e8f5e9", color: darkMode ? "#a5d6a7" : "#2e7d32", border: "1px solid #66bb6a" },
    moving: { bgcolor: darkMode ? "rgba(255,167,38,0.25)" : "#fff3e0", color: darkMode ? "#ffcc80" : "#e65100", border: "2px solid #ffa726" },
    conflict: { bgcolor: darkMode ? "rgba(239,83,80,0.2)" : "#ffebee", color: darkMode ? "#ef9a9a" : "#c62828", border: "1px solid #ef5350" },
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: { xs: 1.5, sm: 2 }, py: 1, flexShrink: 0 }}>
        <SwapHoriz color="primary" />
        <Typography variant="h6" fontWeight={700} component="span">스케줄 플래너</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: { xs: "none", md: "block" } }}>
          회원 칩을 드래그해 놓거나, 클릭 → 옮길 칸 클릭 · 자리가 겹치면 맞바꾸기/체인 · 마지막에 한 번에 적용
        </Typography>
      </Box>

      <Box sx={{ flex: 1, borderTop: 1, borderColor: "divider", display: "flex", flexDirection: { xs: "column", md: "row" }, minHeight: 0 }}>
        {/* 보드 */}
        <Box sx={{ flex: 1, minWidth: 0, p: { xs: 1, sm: 1.5 }, overflow: "auto" }}>
          {notice && (
            <Alert severity={notice.severity} onClose={() => setNotice(null)} sx={{ mb: 1, whiteSpace: "pre-line" }}>
              {notice.message}
            </Alert>
          )}
          {moving && !dragging && (
            <Alert
              severity="info"
              icon={<TouchApp />}
              action={<Button size="small" color="inherit" onClick={() => setMoving(null)}>취소</Button>}
              sx={{ mb: 1, py: 0.25 }}
            >
              <b>{moving.memberName}</b> ({DAY_LABEL[moving.slot.day]} {String(moving.slot.start).slice(0, 5)} · {moving.slot.teacherName}) 이동 중 — 옮길 칸을 클릭하세요
            </Alert>
          )}

          {loading ? (
            <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
          ) : (
            <Box sx={{
              display: "grid",
              // 모바일(md 미만): 한 화면 폭에 주 전체가 들어오도록 축소 · PC(md 이상): 주 전체가 한눈에 들어오는 컴팩트 크기
              gridTemplateColumns: {
                xs: `clamp(30px, 8vw, 44px) repeat(${DAYS.length}, minmax(0, 1fr))`,
                md: `56px repeat(${DAYS.length}, minmax(110px, 1fr))`,
              },
              gridAutoRows: { xs: "minmax(40px, auto)", md: "minmax(30px, auto)" },
              minWidth: { md: 740 },
              border: 1, borderColor: "divider", borderRadius: 1, overflow: "clip",
            }}>
              <Box sx={{ position: "sticky", top: 0, zIndex: 2, bgcolor: darkMode ? "grey.900" : "grey.100", borderBottom: 1, borderColor: "divider" }} />
              {DAYS.map((d) => (
                <Box key={d} sx={{ position: "sticky", top: 0, zIndex: 2, p: { xs: 0.4, md: 0.5 }, textAlign: "center", borderBottom: 1, borderLeft: 1, borderColor: "divider", bgcolor: darkMode ? "grey.900" : "grey.100" }}>
                  <Typography sx={{ fontSize: { xs: "0.75rem", md: "0.85rem" } }} fontWeight={700}>{DAY_LABEL[d]}</Typography>
                </Box>
              ))}

              {timeSlots.map((time) => (
                <React.Fragment key={time}>
                  <Box sx={{ p: { xs: 0.25, md: 0.25 }, display: "flex", alignItems: "center", justifyContent: "center", borderBottom: 1, borderColor: "divider", bgcolor: darkMode ? "grey.900" : "grey.50" }}>
                    <Typography sx={{ fontSize: { xs: "0.58rem", md: "0.75rem" } }} fontWeight={600}>{time}</Typography>
                  </Box>
                  {DAYS.map((day) => {
                    const entries = board.get(`${day}|${time}`) || [];
                    const working = moving ? teachersWorkingAt(day, time) : [];
                    const targetable = moving && working.length > 0;
                    // 근무 선생님 중 한 명이라도 빈자리가 있으면 자유 이동 가능(초록), 전원 점유면 맞바꾸기/체인 필요(빨강)
                    const hasFree = targetable && working.some((t) => occupantsOf(day, time, t.id, moving.memberId).length === 0);
                    const dimmed = moving && !targetable;
                    const simConflict = simConflictCells.has(`${day}|${time}`);
                    const cellKey = `${day}|${time}`;
                    // 드래그 중 :hover가 동작하지 않으므로 호버 강조는 dragOverCell 상태로 처리
                    const isDragOver = targetable && dragOverCell === cellKey;
                    return (
                      <Box
                        key={`${day}-${time}`}
                        onClick={targetable ? () => stageMove(moving, day, time) : undefined}
                        onDragOver={targetable ? (ev) => {
                          ev.preventDefault(); // 드롭 허용
                          ev.dataTransfer.dropEffect = "move";
                          if (dragOverCell !== cellKey) setDragOverCell(cellKey);
                        } : undefined}
                        onDragLeave={targetable ? (ev) => {
                          if (ev.relatedTarget && ev.currentTarget.contains(ev.relatedTarget)) return; // 칸 내부(칩)로의 이동은 무시
                          setDragOverCell((cur) => (cur === cellKey ? null : cur));
                        } : undefined}
                        onDrop={targetable ? (ev) => {
                          ev.preventDefault();
                          setDragOverCell(null);
                          dropHandledRef.current = true;
                          stageMove(moving, day, time); // 이후 흐름은 클릭과 동일 (선생님 선택/맞바꾸기/체인)
                        } : undefined}
                        sx={{
                          p: { xs: 0.2, md: 0.25 }, display: "flex", flexDirection: "column", gap: { xs: 0.25, md: 0.25 }, justifyContent: "center",
                          borderBottom: 1, borderLeft: 1, borderColor: "divider",
                          cursor: targetable ? "copy" : "default",
                          bgcolor: targetable
                            ? (hasFree
                              ? (darkMode
                                ? (isDragOver ? "rgba(76,175,80,0.18)" : "rgba(76,175,80,0.08)")
                                : (isDragOver ? "rgba(76,175,80,0.14)" : "rgba(76,175,80,0.07)"))
                              : (darkMode
                                ? (isDragOver ? "rgba(239,83,80,0.18)" : "rgba(239,83,80,0.08)")
                                : (isDragOver ? "rgba(239,83,80,0.13)" : "rgba(239,83,80,0.06)")))
                            : "transparent",
                          opacity: dimmed ? 0.3 : 1,
                          boxShadow: simConflict ? "inset 0 0 0 2px #ef5350" : "none",
                          "&:hover": targetable
                            ? { bgcolor: hasFree
                              ? (darkMode ? "rgba(76,175,80,0.18)" : "rgba(76,175,80,0.14)")
                              : (darkMode ? "rgba(239,83,80,0.18)" : "rgba(239,83,80,0.13)") }
                            : {},
                        }}
                      >
                        {entries.map((e, i) => {
                          const isMoving = moving && moving.memberId === e.memberId && !e.ghost
                            && moving.slot.day === e.slot.day && String(moving.slot.start).slice(0, 5) === String(e.slot.start).slice(0, 5);
                          // 이동 중 + 이 칸에 빈 선생님 자리가 없으면 점유 회원 = 충돌 상대 (빨강)
                          const isConflictTarget = moving && targetable && !hasFree && !e.ghost && e.memberId !== moving.memberId;
                          // 활성 회원인데 슬롯 선생님이 비활성 — 다른 자리로 옮겨야 할 대상
                          const teacherGone = !e.ghost && e.teacherActive === false;
                          const style = e.ghost
                            ? { bgcolor: "transparent", color: "text.disabled", border: "1px dashed", borderColor: "divider", textDecoration: "line-through" }
                            : isMoving ? chipColor.moving
                            : isConflictTarget ? chipColor.conflict
                            : e.staged ? chipColor.staged : chipColor.base;
                          const canDrag = isDesktop && !e.ghost && e.isActive;
                          return (
                            <Box
                              key={`${e.memberId}-${i}-${e.ghost ? "g" : "s"}`}
                              draggable={canDrag}
                              onDragStart={canDrag ? (ev) => {
                                ev.dataTransfer.setData("text/plain", String(e.memberId)); // Firefox: setData 없으면 드래그가 시작되지 않음
                                ev.dataTransfer.effectAllowed = "move";
                                dropHandledRef.current = false;
                                setDragging(true);
                                setMoving({ memberId: e.memberId, memberName: e.name, slot: { ...e.slot, start: String(e.slot.start).slice(0, 5) } });
                              } : undefined}
                              onDragEnd={canDrag ? () => {
                                setDragging(false);
                                setDragOverCell(null);
                                // 칸 위 드롭으로 이어지지 않은 드래그(ESC·바깥에 놓음)는 이동 모드 해제
                                if (!dropHandledRef.current) setMoving(null);
                                dropHandledRef.current = false;
                              } : undefined}
                              onClick={(ev) => {
                                ev.stopPropagation();
                                if (e.ghost || !e.isActive) return;
                                if (moving && moving.memberId !== e.memberId) {
                                  // 이동 중 다른 회원 칩 클릭 = 그 칸을 대상으로 지정
                                  stageMove(moving, day, time);
                                  return;
                                }
                                if (isMoving) { setMoving(null); return; }
                                setMoving({ memberId: e.memberId, memberName: e.name, slot: { ...e.slot, start: String(e.slot.start).slice(0, 5) } });
                              }}
                              sx={{
                                px: { xs: 0.4, md: 0.6 }, py: { xs: 0.25, md: 0.2 }, borderRadius: 1,
                                fontSize: { xs: "0.64rem", md: "0.8rem" }, fontWeight: 600, lineHeight: 1.25,
                                textAlign: "center",
                                cursor: e.ghost || !e.isActive ? "default" : canDrag ? "grab" : "pointer",
                                opacity: e.isActive ? 1 : 0.45,
                                ...style,
                                ...(teacherGone ? { boxShadow: "inset 0 0 0 2px #ef9a9a" } : {}),
                              }}
                              title={`${e.name} · ${e.teacherName}${e.isActive ? "" : " (비활성 회원)"}${teacherGone ? " — 비활성 선생님의 자리입니다. 다른 자리로 옮겨주세요." : ""}`}
                            >
                              <Box sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {/* 모바일: 칸이 좁아 이름 식별이 우선 — 이동 예정 표시는 색상만으로 충분 */}
                                {e.staged && <Box component="span" sx={{ display: { xs: "none", md: "inline" } }}>✦ </Box>}{e.name}
                              </Box>
                              <Box sx={{ opacity: 0.7, fontWeight: 400, fontSize: "0.8em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {teacherGone && "⚠ "}{e.teacherName}
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    );
                  })}
                </React.Fragment>
              ))}
            </Box>
          )}

          {/* 범례 */}
          {!loading && (
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap mt={1}>
              {[
                { t: "이동 중인 회원", ...chipColor.moving },
                { t: "이동 예정", star: true, ...chipColor.staged },
                { t: "충돌 상대 (맞바꾸기/체인)", ...chipColor.conflict },
                { t: "고정 회원", ...chipColor.base },
              ].map((l) => (
                <Stack key={l.t} direction="row" alignItems="center" spacing={0.5}>
                  <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: l.bgcolor, border: l.border || "1px solid", borderColor: "divider" }} />
                  <Typography variant="caption" color="text.secondary">
                    {l.t}{l.star && <Box component="span" sx={{ display: { xs: "none", md: "inline" } }}> ✦</Box>}
                  </Typography>
                </Stack>
              ))}
              {[{ t: "⚠ 비활성 선생님 자리 (옮기기 필요)" }].map((l) => (
                <Stack key={l.t} direction="row" alignItems="center" spacing={0.5}>
                  <Box sx={{ width: 12, height: 12, borderRadius: 0.5, boxShadow: "inset 0 0 0 2px #ef9a9a" }} />
                  <Typography variant="caption" color="text.secondary">{l.t}</Typography>
                </Stack>
              ))}
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Box sx={{ width: 12, height: 12, borderRadius: 0.5, border: "1px dashed", borderColor: "divider" }} />
                <Typography variant="caption" color="text.secondary">이동 전 원래 자리</Typography>
              </Stack>
              <Typography variant="caption" color="text.disabled">
                · 이동 중 흐린 칸 = 근무 선생님 없음 · 초록 칸 = 빈자리 · 빨간 칸 = 전원 점유
              </Typography>
            </Stack>
          )}
        </Box>

        {/* 우측 레일: 변경 계획 + 검증 결과 */}
        <Box sx={{
          width: { xs: "100%", md: 320, lg: 400 }, flexShrink: 0, borderLeft: { md: 1 }, borderTop: { xs: 1, md: 0 }, borderColor: { xs: "divider", md: "divider" },
          p: 1.5, display: "flex", flexDirection: "column", gap: 1.5, overflow: "auto",
        }}>
          <Typography variant="subtitle2" fontWeight={700}>
            변경 계획 ({[...new Set(moves.map((m) => m.memberId))].length}명 · {moves.length}건)
          </Typography>

          {moves.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              아직 계획이 없습니다. 보드에서 회원 칩을 클릭해 이동을 시작하세요.
            </Typography>
          ) : (
            <Stack spacing={0.75}>
              {moves.map((m, idx) => (
                <Stack key={idx} direction="row" alignItems="center" spacing={0.75} sx={{ p: 0.75, borderRadius: 1, bgcolor: darkMode ? "grey.900" : "grey.50", border: 1, borderColor: "divider" }}>
                  <Typography variant="body2" fontWeight={600} noWrap sx={{ minWidth: 0 }}>{m.memberName}</Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {DAY_LABEL[m.from.day]} {m.from.start}
                  </Typography>
                  <ArrowForward sx={{ fontSize: 13, color: "text.disabled" }} />
                  <Typography variant="caption" fontWeight={600} noWrap>
                    {DAY_LABEL[m.to.day]} {m.to.start}
                  </Typography>
                  <Box flex={1} />
                  <IconButton size="small" onClick={() => removeMove(idx)}><DeleteOutline sx={{ fontSize: 16 }} /></IconButton>
                </Stack>
              ))}
            </Stack>
          )}

          <Divider />

          {/* 검증 결과 */}
          {moves.length > 0 && (
            sim.loading ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">최종 상태 검증 중...</Typography>
              </Stack>
            ) : sim.result ? (
              <Stack spacing={1}>
                {sim.result.canProceed ? (
                  <Alert severity="success" sx={{ py: 0.25 }}>최종 상태 충돌 없음 — 적용 가능</Alert>
                ) : (
                  <Alert severity="error" sx={{ py: 0.25 }}>충돌 {allConflicts.length}건 — 해결 후 적용 가능</Alert>
                )}
                {allConflicts.map((c, i) => (
                  <Alert key={`c${i}`} severity="error" variant="outlined" sx={{ py: 0.25, fontSize: "0.78rem" }}>
                    [{c.memberName}] {c.message}
                  </Alert>
                ))}
                {allWarnings.map((w, i) => (
                  <Alert key={`w${i}`} severity="warning" variant="outlined" sx={{ py: 0.25, fontSize: "0.78rem" }}>
                    [{w.memberName}] {w.message}
                    {/* 날짜별 점유 상세 — 어떤 예약 때문에 건너뛰고 보강 티켓이 발행되는지.
                        경고 문구(주황 톤)와 구분되도록 날짜는 배지, 이름은 본문색 볼드로 강조 */}
                    {(w.details || []).length > 0 && (
                      <Stack spacing={0.5} mt={0.75}>
                        {w.details.map((d) => (
                          <Stack key={d.date} direction="row" alignItems="center" spacing={0.75}>
                            <Box component="span" sx={{
                              flexShrink: 0, px: 0.7, py: 0.15, borderRadius: 0.75,
                              fontWeight: 700, fontSize: "0.72rem", whiteSpace: "nowrap",
                              bgcolor: darkMode ? "rgba(255,167,38,0.25)" : "#ffe0b2",
                              color: darkMode ? "#ffcc80" : "#e65100",
                            }}>
                              {fmtDate(d.date)}
                            </Box>
                            <Box component="span" sx={{ fontSize: "0.75rem", color: "text.primary", lineHeight: 1.35, minWidth: 0 }}>
                              {d.occupants.map((o, j) => (
                                <React.Fragment key={j}>
                                  {j > 0 && ", "}
                                  <Box component="span" sx={{ fontWeight: 700 }}>{o.name}</Box>
                                  <Box component="span" sx={{ color: "text.secondary" }}> · {occupantType(o)}</Box>
                                </React.Fragment>
                              ))}
                            </Box>
                          </Stack>
                        ))}
                        {(w.conflictingTotal || 0) > w.details.length && (
                          <Typography variant="caption" color="text.secondary">
                            외 {w.conflictingTotal - w.details.length}일 더 있음
                          </Typography>
                        )}
                      </Stack>
                    )}
                  </Alert>
                ))}
              </Stack>
            ) : null
          )}

          <Box flex={1} />

          <Alert severity="info" variant="outlined" sx={{ py: 0.5, fontSize: "0.75rem" }}>
            적용 시 오늘부터 예약이 재생성됩니다. 자리가 없는 날짜는 건너뛰고 보강 티켓이 자동 발행되며, 수동 예약(보강)은 보존됩니다.
          </Alert>

          <Stack direction="row" spacing={1}>
            <Button size="small" onClick={() => { setMoves([]); setMoving(null); }} disabled={moves.length === 0 || applying}>
              모두 초기화
            </Button>
            <Box flex={1} />
            <Button variant="contained" onClick={handleApplyClick} disabled={!canApply}>
              {applying ? "적용 중..." : `일괄 적용 (${[...new Set(moves.map((m) => m.memberId))].length}명)`}
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* 충돌 프롬프트: 맞바꾸기 / 체인 */}
      <InnerDialog open={!!conflictPrompt} onClose={() => setConflictPrompt(null)} maxWidth="xs" fullWidth>
        <DialogTitle>자리가 겹칩니다</DialogTitle>
        <DialogContent>
          {conflictPrompt && (
            <Stack spacing={1.5}>
              <Typography variant="body2">
                <b>{DAY_LABEL[conflictPrompt.target.day]} {conflictPrompt.target.time}</b> ({conflictPrompt.to.teacherName}) 자리에는{" "}
                <b>{conflictPrompt.occupant.name}</b> 회원이 있습니다.
              </Typography>
              <Alert severity="info" icon={false} sx={{ py: 0.5, fontSize: "0.8rem" }}>
                <b>서로 맞바꾸기</b>: {conflictPrompt.occupant.name} ↔ {conflictPrompt.mover.memberName} 자리 교환<br />
                <b>{conflictPrompt.occupant.name}도 옮기기</b>: 이동을 확정하고 {conflictPrompt.occupant.name}의 새 자리를 이어서 선택 (체인)
              </Alert>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ flexDirection: "column", alignItems: "stretch", gap: 1, px: 3, pb: 2 }}>
          <Button variant="contained" size="large" startIcon={<SwapHoriz />} onClick={handleSwap}>
            서로 맞바꾸기 ({conflictPrompt?.occupant.name} ↔ {conflictPrompt?.mover.memberName})
          </Button>
          <Button variant="contained" color="secondary" size="large" startIcon={<ArrowForward />} onClick={handleChain}>
            {conflictPrompt?.occupant.name}도 다른 자리로 옮기기 (체인)
          </Button>
          <Button onClick={() => setConflictPrompt(null)}>취소</Button>
        </DialogActions>
      </InnerDialog>

      {/* 대상 선생님 선택: 같은 칸에 여러 선생님이 근무할 때 — 다른 선생님 자리로도 이동 가능 */}
      <InnerDialog open={!!teacherPick} onClose={() => setTeacherPick(null)} maxWidth="xs" fullWidth>
        <DialogTitle>어느 선생님 자리로 옮길까요?</DialogTitle>
        <DialogContent>
          {teacherPick && (
            <>
              <Typography variant="body2" color="text.secondary" mb={1.5}>
                {DAY_LABEL[teacherPick.day]} {teacherPick.time} — <b>{teacherPick.mover.memberName}</b> 회원 이동
              </Typography>
              <Stack spacing={1}>
                {teacherPick.options.map((o) => {
                  const isCurrent = o.teacher.id === teacherPick.mover.slot.teacher_id;
                  const free = o.occupants.length === 0;
                  return (
                    <Button
                      key={o.teacher.id}
                      variant={free ? "contained" : "outlined"}
                      color={free ? "success" : "warning"}
                      onClick={() => {
                        const tp = teacherPick;
                        setTeacherPick(null);
                        proceedWithTeacher(tp.mover, tp.day, tp.time, o.teacher);
                      }}
                      sx={{ justifyContent: "space-between", textTransform: "none", px: 2 }}
                    >
                      <span>
                        {o.teacher.name} 선생님{isCurrent ? " (기존 자리 선생님)" : ""}
                      </span>
                      <Typography component="span" variant="caption" sx={{ opacity: 0.9 }}>
                        {free ? "빈자리" : `${o.occupants[0].name} 있음 → 맞바꾸기/체인`}
                      </Typography>
                    </Button>
                  );
                })}
              </Stack>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTeacherPick(null)}>취소</Button>
        </DialogActions>
      </InnerDialog>

      {/* 전환 주 안내: 적용은 오늘부터라 요일 이동 시 이번 주가 평소와 달라질 수 있음 */}
      <InnerDialog open={!!transitionPrompt} onClose={() => setTransitionPrompt(null)} maxWidth="xs" fullWidth>
        <DialogTitle>전환 주 수업 횟수 안내</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <Typography variant="body2">
              일괄 적용은 <b>오늘부터</b> 반영됩니다. 아래 회원은 전환 주(이번 주) 수업이{" "}
              <b>평소 주당 횟수와 달라집니다.</b>
            </Typography>
            {(transitionPrompt || []).map((p, i) => (
              <Alert key={i} severity="warning" icon={false} sx={{ py: 0.5, fontSize: "0.8rem" }}>
                <b>{p.memberName}</b>: {fmtDate(p.weekStartStr)}~{fmtDate(p.weekEndStr)} 주에 기존{" "}
                {p.oldKept}회 + 새 일정 {p.newCreated}회 = <b>총 {p.total}회</b> (평소 주 {p.normal}회 —{" "}
                {p.total > p.normal ? `${p.total - p.normal}회 초과` : `${p.normal - p.total}회 부족`})
              </Alert>
            ))}
            <Typography variant="caption" color="text.secondary">
              다음 주부터 적용하려면 이 회원은 플래너 대신 회원 수정 화면에서 적용 시작일을
              지정해 변경해주세요.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransitionPrompt(null)}>취소</Button>
          <Button variant="contained" color="warning" onClick={() => { setTransitionPrompt(null); handleApply(); }}>
            이대로 적용
          </Button>
        </DialogActions>
      </InnerDialog>

      {/* 이미 사용된 보강 티켓 처리 결정 (회원별 순차 수집 후 재적용) */}
      <MakeupDecisionDialog
        open={!!makeupQueue}
        decision={makeupQueue?.queue[0] || null}
        submitting={applying}
        onCancel={() => {
          if (applying) return;
          setMakeupQueue(null);
          setNotice({ severity: "info", message: "보강 처리 결정이 취소되어 일괄 적용을 중단했습니다." });
        }}
        onConfirm={handleMakeupDecided}
      />
    </Box>
  );
}

export default SchedulePlanner;
